import { memo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../App/redux/store';
import GenericForm from '../../../../../../shared/components/form/GenericForm';
import TextInputRHF from '../../../../../../shared/components/form/TextInputRHF';
import fetchAdPlotLocationsThunk from '../../../../services/thunks/fetchAdPlotLocationsThunk';
import styles from './adDetailsForms.module.scss';
import FormWrapper from './FormWrapper';
import RadioButtonRHF from '../../../../../../shared/components/form/RadioButtonRHF';
import { adDetailsFormTextInputStyles, useRadioButtonStyles } from './muiStyles';
import { useFormContext } from 'react-hook-form';

import SimilarAdPlotLoader from './SimilarAdPlotLoader';
import {
  areaFormValidationSchema,
  landAreaFormValue,
  LandAreaFormValuesType,
} from './validation';
import ValidationButton from './ValidationButton';
import { adActions, getAdState } from '../../../../adSlice';

const areaSearchModeRadioValues = [
  { value: 'exact', label: 'Surface exacte' },
  { value: 'range', label: 'Plage de surface' },
];

function AreaForm({ ad }: { ad: Ad }) {
  const dispatch = useAppDispatch();
  const { adDetailsAreaFormValues } = useAppSelector(getAdState);

  const getDefaultValues = () => {
    const fallback = landAreaFormValue(ad.landArea);
    if (
      adDetailsAreaFormValues &&
      'searchMode' in adDetailsAreaFormValues &&
      'landAreaMin' in adDetailsAreaFormValues &&
      'landAreaMax' in adDetailsAreaFormValues
    ) {
      return adDetailsAreaFormValues;
    }
    return fallback;
  };
  const handleSubmit = (values: LandAreaFormValuesType) => {
    const isRangeSearch = values.searchMode === 'range';
    dispatch(
      fetchAdPlotLocationsThunk({
        inseeCode: ad?.city.inseeCode,
        isLand: ad?.propertyTypology.name === 'Terrain',
        area: isRangeSearch ? ad.landArea : values.landArea,
        minLandArea: isRangeSearch ? values.landAreaMin : null,
        maxLandArea: isRangeSearch ? values.landAreaMax : null,
      }),
    );
    dispatch(adActions.adDetailsAreaFormValuesSet(values));
  };

  const AreaFormWrapper = ({ children }: IChildren) => {
    return (
      <FormWrapper
        className={styles.areaForm}
        title="Recherche par surface de terrain"
      >
        {children}
      </FormWrapper>
    );
  };

  if (ad?.landArea === 0 || ad?.landArea === null) {
    return (
      <AreaFormWrapper>
        <p className={styles.disabledFormMessage}>
          La surface terrain est non définie <br /> pour ce bien
        </p>
      </AreaFormWrapper>
    );
  }

  return (
    <AreaFormWrapper>
      <SimilarAdPlotLoader />
      <GenericForm
        defaultValues={getDefaultValues()}
        onSubmit={handleSubmit}
        schema={areaFormValidationSchema}
      >
        <div className={styles.fieldContainer}>
          <AreaFormFields />
        </div>

        <ValidationButton label="LANCER LA RECHERCHE PAR SURFACE" />
      </GenericForm>
    </AreaFormWrapper>
  );
}

function AreaFormFields() {
  const { watch } = useFormContext<LandAreaFormValuesType>();
  const searchMode = watch('searchMode');
  const textInputClasses = adDetailsFormTextInputStyles();
  const radioButtonClasses = useRadioButtonStyles();

  return (
    <>
      <RadioButtonRHF
        className={styles.radioBtnGroup}
        name="searchMode"
        radioElements={areaSearchModeRadioValues}
        radioProps={{ classes: radioButtonClasses }}
        row
      />

      {searchMode === 'exact' ? (
        <TextInputRHF
          type="number"
          name="landArea"
          variant="outlined"
          label="Surface terrain"
          classes={textInputClasses}
        />
      ) : (
        <div className={styles.areaRangeContainer}>
          <TextInputRHF
            type="number"
            name="landAreaMin"
            variant="outlined"
            label="Surface terrain mini"
            placeholder="Surface terrain mini"
            classes={textInputClasses}
            hideErrorMessage={false}
          />

          <TextInputRHF
            type="number"
            name="landAreaMax"
            variant="outlined"
            label="Surface terrain maxi"
            placeholder="Surface terrain maxi"
            classes={textInputClasses}
            hideErrorMessage={false}
          />
        </div>
      )}
    </>
  );
}

export default memo(AreaForm);
